import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { vanBanRepository } from '../../../lib/repositories/van-ban.repository';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { useToast } from '../../../lib/toast';
import type { GhiDonVi, GhiMau, GhiTichHop } from '../types';

/**
 * Hook của ba màn Cấu hình xử lý văn bản. Danh mục cấu hình ít đổi nên giữ cache 5 phút; ghi xong chỉ
 * vô hiệu đúng khoá của danh mục đó (cùng luồng `load()` sau mỗi thao tác của bản cũ, không tải lại cả
 * trang).
 */
const KHOA = {
  donVi: ['van-ban', 'don-vi'] as const,
  mau: ['van-ban', 'mau'] as const,
  khaNang: ['van-ban', 'mau', 'kha-nang'] as const,
  tichHop: ['van-ban', 'tich-hop'] as const,
  nhatKy: (id: string) => ['van-ban', 'tich-hop', id, 'nhat-ky'] as const,
};
const GIU = 5 * 60_000;

/** Ghi dữ liệu: thành công → vô hiệu cache + toast đã hoàn tất; lỗi → câu lỗi đã dịch. */
function useGhi<V, R>(
  ham: (v: V) => Promise<R>,
  khoa: readonly unknown[],
  thongBao: (v: V) => KhoaDich | null,
  baoLoi = true,
) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: ham,
    onSuccess: (_d, v) => {
      void queryClient.invalidateQueries({ queryKey: khoa });
      const k = thongBao(v);
      if (k) toast.success(t(k));
    },
    onError: (err) => {
      if (baoLoi) toast.error(dichLoi(err));
    },
  });
}

// ─── Danh sách, vai trò các cấp ──────────────────────────────────────────────

export function useDonVi() {
  return useQuery({ queryKey: KHOA.donVi, queryFn: vanBanRepository.donVi.danhSach, staleTime: GIU });
}

/** Lỗi theo trường không toast: form tự gắn dưới ô (`loiTheoTruong`). */
export function useLuuDonVi() {
  return useGhi(
    (v: { id: string | null; vao: GhiDonVi }) =>
      v.id ? vanBanRepository.donVi.sua(v.id, v.vao) : vanBanRepository.donVi.tao(v.vao),
    KHOA.donVi,
    (v) => (v.id ? 'vanBanDonVi.daLuu' : 'vanBanDonVi.daThem'),
    false,
  );
}

export function useXoaDonVi() {
  return useGhi((id: string) => vanBanRepository.donVi.xoa(id), KHOA.donVi, () => 'vanBanDonVi.daXoa');
}

// ─── Mẫu văn bản ─────────────────────────────────────────────────────────────

export function useMauVanBan() {
  return useQuery({ queryKey: KHOA.mau, queryFn: vanBanRepository.mau.danhSach, staleTime: GIU });
}

/** Bản cài đã cấu hình LLM chưa: chưa thì ẩn nút "Tạo xem trước AI". */
export function useKhaNangMau() {
  return useQuery({ queryKey: KHOA.khaNang, queryFn: vanBanRepository.mau.khaNang, staleTime: GIU });
}

export function useLuuMau() {
  return useGhi(
    (v: { id: string | null; vao: GhiMau }) =>
      v.id ? vanBanRepository.mau.sua(v.id, v.vao) : vanBanRepository.mau.tao(v.vao),
    KHOA.mau,
    (v) => (v.id ? 'vanBanMau.daLuu' : 'vanBanMau.daThem'),
    false,
  );
}

export function useXemTruocMau() {
  return useGhi((id: string) => vanBanRepository.mau.xemTruoc(id), KHOA.mau, () => 'vanBanMau.daTaoXemTruoc');
}

export function useXoaMau() {
  return useGhi((id: string) => vanBanRepository.mau.xoa(id), KHOA.mau, () => 'vanBanMau.daXoa');
}

// ─── Cấu hình đầu vào ────────────────────────────────────────────────────────

export function useTichHop() {
  return useQuery({ queryKey: KHOA.tichHop, queryFn: vanBanRepository.tichHop.danhSach, staleTime: GIU });
}

/** Tạo mới không toast: màn hình mở hộp thoại hiện API key một lần thay cho toast. */
export function useLuuTichHop() {
  return useGhi(
    (v: { id: string | null; vao: GhiTichHop }) =>
      v.id ? vanBanRepository.tichHop.sua(v.id, v.vao) : vanBanRepository.tichHop.tao(v.vao),
    KHOA.tichHop,
    (v) => (v.id ? 'vanBanTichHop.daLuu' : null),
    false,
  );
}

export function useCapLaiKhoa() {
  return useGhi((id: string) => vanBanRepository.tichHop.capLaiKhoa(id), KHOA.tichHop, () => null);
}

export function useGuiThuWebhook() {
  const queryClient = useQueryClient();
  const dichLoi = useLoi();
  const toast = useToast();
  return useMutation({
    mutationFn: (id: string) => vanBanRepository.tichHop.guiThu(id),
    onSuccess: (_d, id) => void queryClient.invalidateQueries({ queryKey: KHOA.nhatKy(id) }),
    onError: (err) => toast.error(dichLoi(err)),
  });
}

/** Nhật ký chỉ tải khi người dùng bấm "Xem nhật ký gửi" (`enabled`), như bản cũ. */
export function useNhatKyWebhook(id: string | null, bat: boolean) {
  return useQuery({
    queryKey: KHOA.nhatKy(id ?? '-'),
    queryFn: () => vanBanRepository.tichHop.nhatKy(id!),
    enabled: !!id && bat,
  });
}

export function useXoaTichHop() {
  return useGhi((id: string) => vanBanRepository.tichHop.xoa(id), KHOA.tichHop, () => 'vanBanTichHop.daXoa');
}
